// 2026-09-01 - FormuLearning - COMP-ROUND-DROPDOWN (이슈 #58 S10/#70, M84-61)
// 파일 위치: frontend/src/features/step4/RoundDropdown.tsx
// 목적: 성분표 **위** — 지금 어느 라운드를 보고 있는지 정한다. PPT 주석이
//       *"4단계에서 가장 중요한 부분"* 으로 지목한 컨트롤이다(UI/UX v2.2 §5.9).
//
// 화면 규약 넷.
//   ① 라벨 규칙은 `roundDisplayName()`(api/round.ts)이 이미 쥐고 있다 — `Initial Round` /
//      `Optimization Round #N`. 여기서 다시 짜지 않는다. 배치 수를 괄호로 병기한다.
//   ② `Jump to latest` = **마지막 라운드로 전환**. 라운드가 늘수록 드롭다운을 끝까지
//      내리는 일이 잦아 별도 버튼을 둔다. 이미 마지막이면 비활성이다.
//   ③ **과거 라운드를 읽기 전용으로 잠그지 않는다**(정본 «폐기 승계» 조항). 라운드는
//      언제든 열람·입력할 수 있다 — S10 1차의 역방향 잠금(`M84-48`)은 Step 1·2·3 에만
//      걸리며 이 축과 무관하다.
//   ④ 선택은 **서버 상태**(`step4_data.current_round_id`)를 바꾼다. 화면 상태를 서버에
//      남기는 것이라 조작 이력은 남기지 않는다(round/router.py `set_current_round_`).

import { roundDisplayName, type RoundListItem } from '../../api/round'

interface Props {
  /** `round_no` 오름차순 — initial 이 항상 첫 항목이다. */
  rounds: RoundListItem[]
  /** 보고 있는 라운드. 서버 미지정이면 상위가 마지막 라운드로 해석해 내려보낸다. */
  currentRoundId: string | null
  onSelect: (roundId: string) => void
  /** 전환 왕복 중 — 중복 클릭을 막는다. */
  busy?: boolean
}

export default function RoundDropdown({ rounds, currentRoundId, onSelect, busy }: Props) {
  if (rounds.length === 0) return null

  const latest = rounds[rounds.length - 1]
  const atLatest = currentRoundId === latest.id

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="round-dropdown">
      <h5 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
        Run Review
      </h5>
      <select
        value={currentRoundId ?? ''}
        disabled={busy}
        onChange={(e) => { if (e.target.value) onSelect(e.target.value) }}
        data-testid="round-select"
        className="rounded bg-bg-input px-1.5 py-0.5 text-[11px] outline-none disabled:opacity-50"
        style={{ color: 'var(--color-accent-teal)', border: '1px solid var(--border-soft)' }}
        title="보고 있는 라운드 — 성분표·결과 입력·분석·추천이 이 라운드를 따릅니다"
      >
        {rounds.map((r) => (
          <option key={r.id} value={r.id}>
            {roundDisplayName(r)} ({r.batch_count})
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onSelect(latest.id)}
        disabled={busy || atLatest}
        data-testid="round-jump-latest"
        className="rounded border px-2 py-0.5 text-[10px] font-semibold text-text-body transition-opacity hover:opacity-90 disabled:opacity-40"
        style={{ borderColor: 'var(--border-teal)', backgroundColor: 'var(--bg-chip-teal)' }}
        title={atLatest ? '이미 마지막 라운드입니다' : `${roundDisplayName(latest)} 로 이동`}
      >
        Jump to latest
      </button>
      <span className="text-[10px] text-text-muted">
        전체 {rounds.length} 라운드 · 과거 라운드도 그대로 입력·수정할 수 있습니다.
      </span>
    </div>
  )
}
